<template>
    <div>
		<div class="row">
			<div class="col-sm-12 col-lg-6">
				<TextInput
					v-model="location.address1"
					:label="$t('address')"
					:disabled="displayOnly">
				</TextInput>
				<TextInput
					v-model="location.address2"
					:label="$t('address-line-2')"
					:disabled="displayOnly"
					:srOnly="true">
				</TextInput>
				<TextInput
					v-model="location.address3"
					:label="$t('address-line-3')"
					:disabled="displayOnly"
					:srOnly="true">
				</TextInput>
				<TextInput
					v-model="location.postcode"
					:label="$t('postcode')"
					:disabled="displayOnly">
				</TextInput>
			</div>
			<div class="col-sm-12 col-lg-6">
                <TextInput
					v-model="location.address4"
					:label="$t('town-city')"
					:disabled="displayOnly">
				</TextInput>
				<TextInput
					v-model="location.address5"
					:label="$t('region-county')"
					:disabled="displayOnly">
				</TextInput>
				<SelectInput :options="countries"
					v-model="location.country"
					:indexIsValue="true"
					:label="$t('country')"
					:disabled="displayOnly">
				</SelectInput>
				<button @click="lookup()" class="btn btn-secondary mt-4 ml-auto float-right"
				:disabled="!location.address1">{{ $t('search') }}</button>
			</div>
		</div>
		<div class="row" v-if="(confidencePassed !== null && !confidencePassed)">
			<div class="col-sm-12">
				<p>{{ $t("geoloc-more-specific-or-correct-address") }}</p>
			</div>
		</div>

        <div class="row" v-if="(coords && confidencePassed)">
            <div class="col-sm-12">
                <p>{{ $t("pin-exact-location") }}</p>
            </div>
        </div>

        <div class="row" v-if="showMap">
            <div class="col-sm-12">
                <div id="map"></div>
            </div>
        </div>
    </div>
</template>
<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';

export default {
    props: {
        confidenceMargin: {// c.f. https://opencagedata.com/api#confidence
            type:Number,
            default:10
        },
        zoom: {
            type:Number,
            default:18
        },
        location: Object,
        displayOnly: Boolean
    },
    data() {
        return {
            confidencePassed: null,
            coordsLoaded: false,
            coords: {},
            showMap: false,
            mapInstance: null,
            marker: null,
            countries:{}
        }
    },
    mounted() {
        this.getCountries()
        if (!this.location) return

        this.$emit('change', {
            coordinates: {
                lat: this.location.lat,
                long: this.location.long
            },
            address: {
                address1: this.location.address1,
                address2: this.location.address2,
                address3: this.location.address3,
                address4: this.location.address4,
                address5: this.location.address5,
                postcode: this.location.postcode,
                country: this.location.country
            },
            confidencePassed: true
        })

        this.initializeMap(this.location.lat, this.location.long)
    },
    watch: {
        location: {
            handler(propAddress) {
                if (propAddress.lat && propAddress.long && Object.keys(propAddress).length > 0) {
                    if(this.mapInstance){
                        this.mapInstance.remove();
                        this.marker.remove();
                        this.mapInstance=null;
                        this.marker = null;
                    }
                    this.initializeMap(propAddress.lat, propAddress.long)
                }
            },
            immediate: true,
        },
        displayOnly: {
            handler(newVal){
                if (newVal || !this.marker) return

                this.mapInstance.remove();
                this.marker.remove();
                this.mapInstance=null;
                this.marker = null;
                this.initializeMap(this.location.lat, this.location.long);
            }
        }
    },
    methods: {
        getCountries() {
            this.$cfetch(`${this.$settings.APIBaseURL}/getcountrycodes`, {
					method: "post",
                    locale: "en-GB"
				})
                .then((res) => {
                    if(res.status === 'SUCCESS'){
                        this.countries = res.countrycodes //change to res.response for consistency's sake
                        this.$emit('countries-loaded',this.countries);
                    } else {
                        console.log("Can't fetch countries");
                    }
                })
                .catch(error => {
                    this.$log.error.call(this, error);
                });
        },
        getCoords(){            
            const addressStr =Object.values(this.location).join(' ');
            this.$cfetch(`${this.$settings.APIBaseURL}/geoloc/lookup`, {
                method: "post",
                data:{
                    address: addressStr,
                    confidence_margin: this.confidenceMargin
                }
            })
            .then((res) => {
                if (res.status !== 'SUCCESS' || !res.result.coordinates) throw res;

                this.coords = res.result.coordinates;
                this.confidencePassed = res.result.confidencePassed;

                this.initializeMap(this.coords.lat, this.coords.lng);

                this.$emit('change', {
                        coordinates: this.coords,
                        address: this.location,
                        confidencePassed: this.confidencePassed,
                });
            })
            .catch(error => {
                console.error(error)
                this.$log.error.call(this, error)

                const errorMessages = {
                    NO_ADDRESS: 'geoloc-error-no-address',
                    NO_RESULTS: 'geoloc-error-no-results',
                    INVALID_REQUEST: 'geoloc-error-no-results',
                    AUTH_ERROR: 'geoloc-error-service-unavailable',
                    QUOTA_EXCEEDED: 'geoloc-error-service-unavailable',
                    RATE_LIMITED: 'geoloc-error-rate-limited',
                    NETWORK_ERROR: 'geoloc-error-network',
                };

                const messageKey = errorMessages[error?.errorType] || 'error-loading-map';

                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t(messageKey)
                })
            });
        },
        lookup() {
            if (this.mapInstance) { //Removes the previous map and marker
                this.marker.off('click', 'move', (event) => {
                        this.coords = event.latlng
                })

                // Remove the map and marker from the DOM
                this.mapInstance.remove();
                this.marker.remove();
                // Set their references to null
                this.mapInstance = null;
                this.marker = null;
            }
            if (this.coordsLoaded) {
                this.coords = null;
                this.coordsLoaded = false;
            }
            
            this.getCoords()
            this.coordsLoaded = true;
        },
        initializeMap(latitude, longitude) {
            if (!(latitude && longitude)) return

            this.showMap = true

            this.$nextTick(() => {
                this.mapInstance = L.map('map').setView([latitude, longitude], this.zoom);

                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: 'Map data © OpenStreetMap contributors',
                    maxZoom: 20,
                }).addTo(this.mapInstance);

                this.initializeMarker(latitude, longitude);
            })
            
        },
        initializeMarker(latitude, longitude){
            const fmarker = L.icon({ iconUrl: icon }); // This is provisional until we decide on an icon
            this.marker = L.marker([latitude, longitude],
            { icon: fmarker, draggable: !this.displayOnly ? true : false }).addTo(this.mapInstance);
            if(!this.displayOnly){
            this.marker.on('move',
                (event) => { // Assign new coordinates when the marker moves
                    this.coords = event.latlng;
                    this.$emit('change',
                        {
                            coordinates: this.coords,
                            address: this.location,
                            confidencePassed: this.confidencePassed
                        });
                });
            }
        }
    },
    computed:{
        validatePostcodeUK() {
            // Regular expression to validate UK postcode
            const postcodeRegex = /^[A-Z]{1,2}[0-9][0-9A-Z]?\s?[0-9][A-Z]{2}$/i;

            // Perform the validation using the regular expression
            return postcodeRegex.test(this.location.postcode);
        }
    }
}
</script>

<style scoped>
#map {
    height: 400px;
}
</style>
